import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import type { Session, User, AuthError } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import type { User as Profile } from '../../../shared/src/types/database';

// ─── Types ────────────────────────────────────────────────────────────────────

interface AuthContextValue {
  /** Supabase auth session (contains JWT access_token) */
  session:  Session | null;
  /** Supabase auth user (identity info from auth.users) */
  user:     User    | null;
  /** Application profile from public.users */
  profile:  Profile | null;
  /** True while the initial session is being restored from localStorage */
  loading:  boolean;
  /**
   * True when the user is authenticated but hasn't completed consent.
   * Triggers redirect to /auth/consent.
   */
  needsConsent: boolean;

  signInWithEmail:    (email: string, password: string) => Promise<AuthError | null>;
  signInWithGoogle:   () => Promise<AuthError | null>;
  signInWithApple:    () => Promise<AuthError | null>;
  signOut:            () => Promise<void>;
  /** Re-fetches profile from public.users — call after profile updates */
  refreshProfile:     () => Promise<void>;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue | null>(null);

// ─── Provider ─────────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user,    setUser]    = useState<User    | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  // ── Fetch public.users profile for the current user ──────────────────────
  // Retries up to 4 times with backoff — handles the race where the DB trigger
  // hasn't finished writing the row yet right after signup/login.
  const fetchProfile = useCallback(async (userId: string) => {
    const MAX_ATTEMPTS = 4;
    const DELAY_MS     = 800;

    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        console.error('[AuthContext] Failed to fetch profile:', error.message);
        break;
      }

      if (data) {
        setProfile(data);
        return;
      }

      // Profile not ready yet — wait and retry
      if (attempt < MAX_ATTEMPTS - 1) {
        await new Promise((r) => setTimeout(r, DELAY_MS * (attempt + 1)));
      }
    }

    // All attempts exhausted — set null so the app doesn't hang
    setProfile(null);
  }, []);

  const refreshProfile = useCallback(async () => {
    if (user) await fetchProfile(user.id);
  }, [user, fetchProfile]);

  // ── Initialize session from storage + listen for auth changes ────────────
  useEffect(() => {
    // 1. Restore existing session (fast — reads from localStorage)
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      setUser(s?.user ?? null);
      if (s?.user) {
        fetchProfile(s.user.id).finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    });

    // 2. Subscribe to future auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, s) => {
        setSession(s);
        setUser(s?.user ?? null);

        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          // Fire-and-forget — do NOT await inside this callback.
          // Supabase awaits the callback return value while holding the
          // exchange lock; awaiting fetchProfile (up to 8s of retries)
          // keeps the lock held long enough for the auto-refresh timer to
          // steal it, which kills the exchangeCodeForSession call.
          if (s?.user) fetchProfile(s.user.id);
        }

        if (event === 'SIGNED_OUT') {
          setProfile(null);
        }
      },
    );

    return () => subscription.unsubscribe();
  }, [fetchProfile]);

  // ── Derive needsConsent ───────────────────────────────────────────────────
  // A user needs to complete the consent flow if they're authenticated but
  // their public.users profile is missing the required legal timestamps.
  const needsConsent =
    !!user &&
    !loading &&
    (!profile?.consent_terms_ts || !profile?.consent_sms_ts);

  // ── Auth actions ──────────────────────────────────────────────────────────

  const signInWithEmail = useCallback(
    async (email: string, password: string): Promise<AuthError | null> => {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      return error;
    },
    [],
  );

  const signInWithGoogle = useCallback(async (): Promise<AuthError | null> => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options:  {
        redirectTo: `${window.location.origin}/auth/callback`,
        queryParams: { access_type: 'offline', prompt: 'consent' },
      },
    });
    return error;
  }, []);

  const signInWithApple = useCallback(async (): Promise<AuthError | null> => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'apple',
      options:  { redirectTo: `${window.location.origin}/auth/callback` },
    });
    return error;
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <AuthContext.Provider
      value={{
        session,
        user,
        profile,
        loading,
        needsConsent,
        signInWithEmail,
        signInWithGoogle,
        signInWithApple,
        signOut,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
